import { useEffect, useRef, useState } from 'react';

// PR 4 — state-envelope poller.
//
// Polls GET /api/takeover/sessions/:id/state every 2.5s while the
// session is non-terminal. Returns:
//
//   { state, isReconnecting, hasError, errorKind }
//
// `state` is the most recent successful envelope (null until the first
// poll lands). `isReconnecting` is true after a transient failure
// (5xx / network blip / timeout) — the seller-facing UI uses this to
// keep the prior content rendered with a subtle "reconnecting…"
// indicator instead of flashing a "failed" screen on every network
// blip. `hasError` only flips true on a terminal auth failure
// (401 / 403) where there is no recovery without operator intervention.
//
// PR #405 (2026-05-19) — defense-in-depth against the multi-tab
// idempotency-rejoin token cascade. Refinements:
//   * Read sessionToken FRESHLY on every poll (from localStorage /
//     sessionStorage) instead of capturing the prop in a closure.
//     The prop seeds React state; storage is the source of truth so a
//     sibling tab's token rotation lands here on the next tick.
//   * On 401/403, before flipping `hasError`, check whether storage
//     holds a NEWER token than the one used in the failed request.
//     If yes → retry the same /state request ONCE with the newer
//     token. Only clear the session if the retry also fails.
//   * `storage` event listener bumps a re-poll nonce so cross-tab
//     token rotations apply immediately, not on the next 2.5s tick.
//
// Design constraints from the PR 4 directive:
//   * NEVER show "failed" for a transient failure → on 5xx / timeout
//     the hook keeps the prior state and sets isReconnecting=true.
//   * Stop polling on terminal status — `completed`, `terminal_failed`,
//     `failed` (legacy), `cancelled`, `abandoned`.
//   * Re-poll for new state when the BE rejects a stale challenge.
//     Consumers signal this by bumping the `pollNonce` arg.
//
// The hook owns the polling interval and tears it down on unmount or
// when the session/token disappears. The submit handler is NOT in this
// hook — App.jsx wires the GenericChallenge `onSubmit` prop directly
// to api.submitChallenge so the state hook stays pure read-only.

const POLL_INTERVAL_MS = 2500;
const TERMINAL_STATUSES = new Set([
  'completed',
  'login_completed',
  'terminal_failed',
  'operator_review',
  'failed',
  'cancelled',
  'abandoned',
]);

// PR #405 — read the freshest token from storage (sessionStorage is
// the canonical write target, matches the rest of the FE). Falls back
// to the prop value if storage is unavailable / empty so the hook
// still works in SSR / test scenarios that don't seed storage.
function readLatestToken(propToken) {
  if (typeof window === 'undefined') return propToken || null;
  try {
    const stored = window.sessionStorage.getItem('sessionToken');
    if (stored && typeof stored === 'string' && stored.length > 0) return stored;
  } catch { /* private-mode Safari / quota — fall through */ }
  return propToken || null;
}

/**
 * @param {object} params
 * @param {string|null} params.sessionId
 * @param {string|null} params.sessionToken
 * @param {string}      params.apiBaseUrl
 * @param {boolean=}    params.enabled  default true
 * @param {number=}     params.pollNonce bump to force an immediate re-poll
 * @param {number=}     params.intervalMs override (tests)
 * @param {Function=}   params.fetchImpl override (tests)
 */
export default function useSessionState({
  sessionId,
  sessionToken,
  apiBaseUrl,
  enabled = true,
  pollNonce = 0,
  intervalMs,
  fetchImpl,
}) {
  const [state, setState] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorKind, setErrorKind] = useState(null);

  const stateRef = useRef(state);
  stateRef.current = state;

  // PR #405 — cross-tab token sync. A sibling tab's createSession
  // response writes a new token into sessionStorage (and may broadcast
  // via the `storage` event if other tabs share localStorage). Bump
  // a local nonce on the event so the polling effect re-runs with the
  // freshest token immediately, not on the next 2.5s tick.
  const [storageNonce, setStorageNonce] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (e) => {
      // We care about sessionToken or sessionId changes. Browsers fire
      // `storage` for localStorage cross-tab; sessionStorage is
      // per-tab so the listener is mostly for the sessionId key (the
      // canonical localStorage write). That's still useful: a sibling
      // tab finishing createSession will at minimum confirm sessionId,
      // and our readLatestToken() pulls the freshest token from the
      // current tab's sessionStorage on the next poll.
      if (!e || !e.key) return;
      if (e.key === 'sessionToken' || e.key === 'takeover.sessionId' || e.key === 'takeover.lastCreateAt') {
        setStorageNonce((n) => n + 1);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    // PR #405 — gate on (sessionId && a token from EITHER prop or
    // sessionStorage). If a sibling tab just rotated the token but
    // the prop hasn't propagated yet, storage still has it and we
    // want to keep polling rather than tearing the timer down.
    const initialToken = readLatestToken(sessionToken);
    if (!enabled || !sessionId || !initialToken || !apiBaseUrl) {
      // Clear any prior reconnecting / error state when the session
      // goes away — avoids stale "reconnecting…" indicators after a
      // log-out / hydration-failed clear.
      setIsReconnecting(false);
      setHasError(false);
      setErrorKind(null);
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    const doFetch = fetchImpl || (typeof window !== 'undefined' ? window.fetch.bind(window) : null);
    if (!doFetch) return undefined;

    const url = `${apiBaseUrl}/api/takeover/sessions/${sessionId}/state`;

    const poll = async () => {
      // PR #405 — read FRESH token on each poll. A sibling tab's
      // createSession rotation lands in sessionStorage before the
      // prop update reaches this hook; reading at fetch time means
      // we never use a stale closure-captured token.
      const tokenUsed = readLatestToken(sessionToken);
      if (!tokenUsed) {
        // Token vanished from storage (logout / clear) — surface as
        // transient reconnecting; the next render will tear the
        // effect down via the enabled-gate above.
        setIsReconnecting(true);
        return;
      }
      try {
        const res = await doFetch(url, {
          headers: { 'X-Session-Token': tokenUsed },
        });
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          // PR #405 — DEFENSIVE 401/403 handling. Before flipping
          // hasError (which clears the session), check whether
          // storage now holds a NEWER token than the one we just
          // used. The multi-tab repro is: tab A polls with T_a, tab
          // B creates with same idempotencyKey, BE rotates to T_b
          // and writes T_b to sessionStorage; tab A's NEXT poll uses
          // T_a (closure cycle) and 401s. With the fresh-read above,
          // the same-tab case is already covered; this branch
          // handles the race where storage was updated BETWEEN our
          // read and the BE's auth check.
          const freshAfterFail = readLatestToken(sessionToken);
          if (freshAfterFail && freshAfterFail !== tokenUsed) {
            // Retry ONCE with the newer token. No log of token plaintext.
            // eslint-disable-next-line no-console
            console.warn('[useSessionState] 401 with stale token; retrying with newer token from storage');
            try {
              const retry = await doFetch(url, {
                headers: { 'X-Session-Token': freshAfterFail },
              });
              if (cancelled) return;
              if (retry.status === 401 || retry.status === 403) {
                // Still 401 with the fresh token → genuinely stale.
                setHasError(true);
                setErrorKind('unauthorized');
                if (timer) { clearInterval(timer); timer = null; }
                return;
              }
              if (!retry.ok) {
                setIsReconnecting(true);
                return;
              }
              const body = await retry.json();
              if (cancelled) return;
              setState(body);
              setIsReconnecting(false);
              setHasError(false);
              setErrorKind(null);
              if (body && TERMINAL_STATUSES.has(body.status)) {
                if (timer) { clearInterval(timer); timer = null; }
              }
              return;
            } catch (_retryErr) {
              if (cancelled) return;
              // Retry network failure — surface as transient. We do
              // NOT clear the session on a network blip during retry.
              setIsReconnecting(true);
              return;
            }
          }
          // No newer token available → terminal. Stop polling. App.jsx
          // reacts to hasError to clear localStorage + bounce to welcome.
          setHasError(true);
          setErrorKind('unauthorized');
          if (timer) { clearInterval(timer); timer = null; }
          return;
        }
        if (!res.ok) {
          // 5xx / 4xx-other — transient, surface the indicator but keep
          // the prior state. Per directive: NEVER flip the FE to a
          // "failed" screen because of a poll failure.
          setIsReconnecting(true);
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setState(body);
        setIsReconnecting(false);
        setHasError(false);
        setErrorKind(null);
        // Terminal status — stop polling. Consumers still get the final
        // state; we just don't keep hitting the BE for a session that
        // can't change.
        if (body && TERMINAL_STATUSES.has(body.status)) {
          if (timer) { clearInterval(timer); timer = null; }
        }
      } catch (err) {
        if (cancelled) return;
        // Network drop / fetch reject — transient.
        setIsReconnecting(true);
      }
    };

    // Fire immediately, then on the interval. The first poll lands
    // ~0ms after mount; subsequent polls every intervalMs.
    poll();
    timer = setInterval(poll, intervalMs || POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // pollNonce + storageNonce force an immediate re-poll after a
    // stale-challenge response OR a cross-tab token rotation.
  }, [enabled, sessionId, sessionToken, apiBaseUrl, intervalMs, pollNonce, storageNonce, fetchImpl]);

  return { state, isReconnecting, hasError, errorKind };
}

// Expose terminal set for tests + App.jsx routing.
export const TERMINAL_STATUS_SET = TERMINAL_STATUSES;
